import React, { useEffect, useState } from "react";
import { Layout, Form, Typography } from "antd";
import { useDispatch } from 'react-redux';
import { MailOutlined } from '@ant-design/icons';
import { UiButton, UiTextBox } from '../../components';
import { showSuccessNotification, showErrorNotification } from '../../components/uiNotification';
import * as api from '../../actions';
import { USERMANAGEMENT, ADMINUSERMANAGEMENT, OTP_SERVICE } from '../../apiServices/endpoints';
import { useMutation } from '@tanstack/react-query';
import Loginform from './Loginform/index';
import ForgetPassword from './Forgetpassword/index';
import SetPassword from './SetPassword/index';
import Logo from '../../assets/images/Logo.png';
import "./style.scss";
import { getconfigList } from "../../reducer/config.reducer";

const { Content } = Layout;
const { Link } = Typography;

const Login = () => {
    const [form] = Form.useForm();
    const [setPasswordForm] = Form.useForm();
    const dispatch = useDispatch();

    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [showEnterOtp, setShowEnterOtp] = useState(false);
    const [showSetPassword, setShowSetPassword] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [resetToken, setResetToken] = useState('');
    const [countdown, setCountdown] = useState(60);
    const [canResend, setCanResend] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSetPasswordFormValid, setIsSetPasswordFormValid] = useState(false);
    const [, setRememberMe] = useState(false);
    const isFormValid = username?.trim() && password?.trim();
    const isOtpValid = verificationCode?.trim().length === 6 && /^[0-9]+$/.test(verificationCode?.trim());

    const loginUser = (payload) => api.post(`${USERMANAGEMENT}/admin_login`, payload);
    const forgotPassword = (mobile) => api.post(`${ADMINUSERMANAGEMENT}/forgetPassword/91${mobile}`);
    const verifyOtp = (payload) => api.post(`${OTP_SERVICE}/verify/forget-password_admin`, payload);
    const resetPassword = (payload) => api.post(`${USERMANAGEMENT}/setPassword`, payload);

    const { mutate: doLogin, isFetching: isLoginFetching } = useMutation({
        mutationKey: ['LOGIN_USER'],
        mutationFn: loginUser,
        onSuccess: (response) => {
            const data = response?.data || response?.response?.data || response;
            const statusCode = data?.statusCode || response?.statusCode || response?.response?.status;
            const loginInfo = data?.result?.login;
            if (statusCode === 200) {
                dispatch(getconfigList());
                showSuccessNotification('Login Successful', 'Welcome back! You have been logged in successfully.');
                if (loginInfo?.token) {
                    sessionStorage.setItem('_ac', loginInfo.token);
                    sessionStorage.setItem('userId', loginInfo.userId);
                    sessionStorage.setItem('role', loginInfo.role);
                    window.location.href = '/Dashboard';
                }
            } else {
                showErrorNotification('Login Failed', data?.error?.message);
            }
        },
        onError: (error) => {
            const data = error?.response?.data || error;
            showErrorNotification('Login Failed', data?.error?.message);
        },
    });

    const { mutate: doForgotPassword, isFetching: isForgotPasswordFetching } = useMutation({
        mutationKey: ['FORGOT_PASSWORD'],
        mutationFn: forgotPassword,
        onSuccess: (response) => {
            const data = response?.data || response;
            const statusCode = data?.statusCode || response?.statusCode;
            if (statusCode === 200) {
                showSuccessNotification('OTP Sent Successfully', `We've sent a verification code to ${phoneNumber}. Please check your messages.`);
                setShowForgotPassword(false);
                setShowEnterOtp(true);
            } else {
                showErrorNotification('Failed to Send OTP', data?.error?.message || 'Unable to send OTP. Please try again.');
            }
        },
    });

    const { mutate: doVerifyOtp, isFetching: isVerifyOtpFetching } = useMutation({
        mutationKey: ['VERIFY_OTP'],
        mutationFn: verifyOtp,
        onSuccess: (response) => {
            const data = response?.data || response;
            const statusCode = data?.statusCode || response?.statusCode;
            const token = data?.result?.['OTP verified']?.resetToken || data?.result?.verifyOtp?.resetToken;
            if (statusCode === 200) {
                showSuccessNotification('OTP Verified Successfully', 'Your phone number has been verified. You can now reset your password.');
                if (token) {
                    setResetToken(token);
                }
                setShowEnterOtp(false);
                setShowSetPassword(true);
            } else {
                showErrorNotification('Invalid OTP', data?.error?.message || 'The verification code you entered is incorrect. Please try again.');
            }
        },
    });

    const { mutate: doResetPassword, isFetching: isResetPasswordFetching } = useMutation({
        mutationKey: ['RESET_PASSWORD'],
        mutationFn: resetPassword,
        onSuccess: (response) => {
            const data = response?.data || response;
            const statusCode = data?.statusCode || response?.statusCode;
            if (statusCode === 200) {
                showSuccessNotification('Password Reset Successfully', 'Your password has been updated. You can now log in with your new password.');
                handleBackToLogin();
            } else {
                showErrorNotification('Failed to Reset Password', data?.error?.message || 'Unable to reset password. Please try again.');
            }
        },
    });

    const handleForgotPassword = () => setShowForgotPassword(true);
    const handleBackToLogin = () => {
        setShowForgotPassword(false);
        setShowEnterOtp(false);
        setShowSetPassword(false);
        setPhoneNumber('');
        setVerificationCode('');
        setResetToken('');
    };

    const handleLoginSubmit = (values) => {
        const { username: user, password: pass, remember } = values || {};
        if (!user?.trim() || !pass?.trim()) {
            showErrorNotification('Validation Error', 'Username and password are required.');
            return;
        }
        setUsername(user);
        setPassword(pass);
        setRememberMe(remember || false);
        if (remember) {
            sessionStorage.setItem('mlawyer_credentials', JSON.stringify({ username: user, password: pass }));
        } else {
            sessionStorage.removeItem('mlawyer_credentials');
        }
        const payload = { mobileNumber: `91${user}`, password: pass };
        doLogin(payload);
    };

    const handleForgotSubmit = (values) => {
        const { phoneNumber: phone } = values || {};
        if (!phone?.trim() || phone.trim().length < 10 || !/^[0-9]+$/.test(phone.trim())) {
            showErrorNotification('Validation Error', 'Enter a valid 10-digit phone number.');
            return;
        }
        setPhoneNumber(phone);
        doForgotPassword(phone);
    };

    const onOtpSubmit = (values) => {
        if (!verificationCode || verificationCode.trim() === '') {
            showErrorNotification('Validation Error', 'Verification code is required.');
            return;
        }
        if (verificationCode.trim().length !== 6) {
            showErrorNotification('Validation Error', 'Verification code must be 6 digits long.');
            return;
        }
        const payload = {
            mobileNumber: `91${phoneNumber}`,
            otp: verificationCode.trim(),
        };
        doVerifyOtp(payload);
    };

    const onOtpFailed = (errorInfo) => {
        const firstError = errorInfo?.errorFields?.[0];
        const errorMessage = firstError?.errors?.[0] || 'Please fill in all required fields correctly.';
        showErrorNotification('Validation Error', errorMessage);
    };

    const onSetPasswordSubmit = (values) => {
        if (!values) {
            showErrorNotification('Validation Error', 'No form data provided.');
            return;
        }
        const { password, confirmPassword } = values;
        if (!password || !confirmPassword) {
            showErrorNotification('Validation Error', 'Both password fields are required.');
            return;
        }
        if (password !== confirmPassword) {
            showErrorNotification('Validation Error', 'Passwords do not match.');
            return;
        }
        if (password.length < 8) {
            showErrorNotification('Validation Error', 'Password must be at least 8 characters long.');
            return;
        }
        if (password.length > 16) {
            showErrorNotification('Validation Error', 'Password cannot exceed 16 characters.');
            return;
        }
        if (!/(?=.*[A-Z])(?=.*[!@#$%^&*]).{8,16}/.test(password)) {
            showErrorNotification('Validation Error', 'Password must contain at least one uppercase letter and one special character.');
            return;
        }
        if (!resetToken) {
            showErrorNotification('Error', 'Reset token is missing. Please verify OTP again.');
            return;
        }
        const payload = {
            mobileNumber: `91${phoneNumber}`,
            password: password.trim(),
            resetToken: resetToken,
            role: 'ADMIN'
        };
        doResetPassword(payload);
    };

    const handleResendCode = () => {
        if (!canResend) return;
        setCountdown(60);
        setCanResend(false);
        doForgotPassword(phoneNumber);
    };

    const handleSetPasswordFormChange = (changedFields, allFields) => {
        const password = setPasswordForm.getFieldValue('password');
        const confirmPassword = setPasswordForm.getFieldValue('confirmPassword');
        const isValid =
            password &&
            confirmPassword &&
            password.length >= 8 &&
            password.length <= 16 &&
            password === confirmPassword &&
            /(?=.*[A-Z])(?=.*[!@#$%^&*]).{8,16}/.test(password);
        setIsSetPasswordFormValid(isValid);
    };

    const handleSetPasswordFailed = () => {
        setIsSetPasswordFormValid(false);
    };

    useEffect(() => {
        if (!showEnterOtp) return;
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown((count) => count - 1), 1000);
            return () => clearTimeout(timer);
        }
        setCanResend(true);
    }, [countdown, showEnterOtp]);

    useEffect(() => {
        const savedCredentials = sessionStorage.getItem('mlawyer_credentials');
        if (savedCredentials) {
            try {
                const { username: savedUsername, password: savedPassword } = JSON.parse(savedCredentials);
                setUsername(savedUsername || '');
                setPassword(savedPassword || '');
                setRememberMe(true);
                form.setFieldsValue({
                    username: savedUsername || '',
                    password: savedPassword || '',
                    remember: true,
                });
            } catch (error) {
                showErrorNotification('Error', 'Error loading saved credentials');
            }
        }
    }, [form]);

    return (
        <Layout className="login-layout">
            <Content className="login-content">
                <div className="login-container">
                    <div className="login-left">
                        <div className="login-logo-container">
                            <img src={Logo} alt="Logo" className="login-logo-img" />
                            <div level={1} className="login-logo-title">MLawyer</div>
                            <div className="login-logo-subtitle-1">
                                Smart <span className="login-logo-subtitle-highlight">Legal Intelligence</span>
                            </div>
                        </div>
                    </div>
                    <div className="login-right">
                        <div className="login-content-container">
                            <div className={`login-content-welcome-card`}>
                                <div className="login-content-form-card">
                                    {showSetPassword ? (
                                        <SetPassword
                                            onSubmit={onSetPasswordSubmit}
                                            loading={isResetPasswordFetching}
                                            showPassword={showPassword}
                                            setShowPassword={setShowPassword}
                                            showConfirmPassword={showConfirmPassword}
                                            setShowConfirmPassword={setShowConfirmPassword}
                                            isFormValid={isSetPasswordFormValid}
                                            onFormChange={handleSetPasswordFormChange}
                                            onFormFailed={handleSetPasswordFailed}
                                            form={setPasswordForm}
                                        />
                                    ) : showEnterOtp ? (
                                        <>
                                            <div className="forget-password-content-title">Forgot Your Password</div>
                                            <div className="forget-password-content-subtitle">
                                                Enter your registered Mobile Number. We'll send you a OTP.
                                            </div>
                                            <Form
                                                form={form}
                                                name="enterOtp"
                                                onFinish={onOtpSubmit}
                                                onFinishFailed={onOtpFailed}
                                                autoComplete="off"
                                                layout="vertical"
                                                size="large"
                                                className="forget-password-form"
                                            >
                                                <Form.Item
                                                    name="verificationCode"
                                                    rules={[
                                                        { required: true, message: 'Please enter the verification code' },
                                                        { len: 6, message: 'Verification code must be exactly 6 digits' },
                                                        { pattern: /^[0-9]+$/, message: 'Verification code can only contain numbers' },
                                                        { whitespace: true, message: 'Verification code cannot be empty' },
                                                    ]}
                                                    className="forget-password-form-item"
                                                    style={{ padding: '0px 0px 0px 0px' }}
                                                >
                                                    <UiTextBox
                                                        prefix={<MailOutlined />}
                                                        placeholder="Verification code"
                                                        className="forget-password-input"
                                                        required={true}
                                                        type="tel"
                                                        maxLength={6}
                                                        value={verificationCode}
                                                        onChange={(event) => setVerificationCode(event.target.value)}
                                                        inputMode="numeric"
                                                        onKeyPress={(event) => !/[0-9]/.test(event.key) && event.preventDefault()}
                                                    />
                                                </Form.Item>
                                                <Form.Item className="forget-password-form-item">
                                                    <UiButton
                                                        type="primary"
                                                        htmlType="submit"
                                                        disabled={!isOtpValid}
                                                        loading={isVerifyOtpFetching}
                                                        className={`forget-password-submit-button ${isOtpValid ? 'forget-password-submit-button-enabled' : ''}`}
                                                    >
                                                        Submit
                                                    </UiButton>
                                                </Form.Item>
                                                <Form.Item className="forget-password-form-item-resend">
                                                    <div className="resend-code-container">
                                                        {canResend ? (
                                                            <Link onClick={handleResendCode} className="resend-code-link">Resend code</Link>
                                                        ) : (
                                                            <span className="resend-countdown">Resend code in {countdown}s</span>
                                                        )}
                                                    </div>
                                                </Form.Item>
                                                <Form.Item className="forget-password-form-item-last">
                                                    <UiButton type="default" onClick={handleBackToLogin} className="forget-password-back-button">
                                                        Back Login
                                                    </UiButton>
                                                </Form.Item>
                                            </Form>
                                        </>
                                    ) : showForgotPassword ? (
                                        <ForgetPassword
                                            onBackToLogin={handleBackToLogin}
                                            onSubmit={handleForgotSubmit}
                                            loading={isForgotPasswordFetching}
                                            phoneNumber={phoneNumber}
                                            onPhoneNumberChange={(event) => setPhoneNumber(event.target.value)}
                                            isPhoneValid={phoneNumber?.trim().length >= 10 && /^[0-9]+$/.test(phoneNumber?.trim())}
                                        />
                                    ) : (
                                        <Loginform
                                            onForgotPassword={handleForgotPassword}
                                            onSubmit={handleLoginSubmit}
                                            username={username}
                                            password={password}
                                            onUsernameChange={(event) => setUsername(event.target.value)}
                                            onPasswordChange={(event) => setPassword(event.target.value)}
                                            loading={isLoginFetching}
                                            isFormValid={isFormValid}
                                            form={form}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Content>
        </Layout>
    );
};

export default Login;
